import React from 'react';

import type { CardContext } from '@atlaskit/link-provider/context';
import type { EditorProps } from '@atlaskit/editor-core/editor';
import type { FullPageEditorProps as FullPageEditorPropsInitial } from '@atlaskit/editor-core/appearance-editor-full-page';
import { FullPageEditor } from '@atlaskit/editor-core/appearance-editor-full-page';
import type { ContextualFormattingEnabledOptions } from '@atlaskit/editor-common/toolbar';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode/editorViewmodePluginType';
import type { ContextPanelPluginOptions } from '@atlaskit/editor-plugin-context-panel/contextPanelPluginType';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension/extensionPluginType';
import type { ExtensionConfiguration } from '@atlaskit/editor-plugin-selection-extension/types';
import type { GetPMNodeHeight } from '@atlaskit/editor-common/extensibility/types';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';

import type { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { ReferentialityContext } from '@atlassian/editor-referentiality/types';

import type { FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import type { AIGlobalOptIn } from '@confluence/editor-features/entry-points/AIGlobalOptIn.ts';
import type { EditorPluginAIFeedbackHandlersRef } from '@confluence/editor-features/entry-points/EditorPluginAIFeedbackHandlersRef.ts';
// The new one will come soon in the next PR in @confluence/editor-presets
// which will avoid using universal preset
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';
import {
	useFullPageEditorPreset,
	type FullPageEditorPresetAPI,
} from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';

export interface FullPageEditorProps extends EditorProps {
	referentialityContext: ReferentialityContext;
	editorPluginAIOptions?: ReturnType<typeof createPageEditorPluginAIOptions>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	editorPluginAIFeedbackHandlers: React.RefObject<EditorPluginAIFeedbackHandlersRef>;
	setEditorAPI?: (editorAPI?: FullPageEditorPresetAPI) => void;
	viewMode?: ViewMode;
	hasEditPermission?: boolean;
	requestToEditContent?: () => void;
	hasRequestedEditPermission?: boolean;
	appearance: FullPageAppearances;
	smartCardContext?: CardContext;
	getHeadingLinkPath: () => string | null;
	shouldHideHeadingAnchorLinks?: boolean;
	__rendererExtensionOptions?: ExtensionPluginOptions['__rendererExtensionOptions'];
	__isRemixHidden?: boolean;
	__isRovoLLMEnabled?: boolean;
	__remixDisabledReason?: 'archived' | 'version-history';
	__livePage?: boolean;
	contextPanelOptions?: ContextPanelPluginOptions;
	spaceId: string | undefined;
	spaceKey: string | undefined;
	contentId: string | undefined;
	isAIEnabledForContent: boolean;
	getExtensionHeight?: GetPMNodeHeight;
	showEmbeddedModernHeader?: boolean;
	contextualFormattingMode?: ContextualFormattingEnabledOptions;
	enableAIPlannerRemoteTextHighlight?: boolean;
	isCompanyHub: boolean;
	snippetExtensionList?: ExtensionConfiguration[];
	syncedBlockEventHandlers?: EventHandlers;
	isEmbeddedConfluence?: boolean;
	contentType?: string;
	onShowSuggestionsSidebar?: () => void;
}

// We have a number of legacy props (cleanup soon) we don't want to bring over to the new API
// So we have them on the Confluence side only
type PatchedFullPageEditorProps = EditorProps &
	FullPageEditorPropsInitial & {
		__livePage?: boolean;
	};

type FullPageEditorPropsNext = FullPageEditorProps & WithFlagsProps;
// Once we cleaned up the legacy props, we can use below type for FullPageEditorPropsNext
// type FullPageEditorPropsNext = PatchedFullPageEditorProps & FullPageEditorPresetProps;

/**
 * ComposableEditor component which uses the full page editor preset
 */
export function FullPageComposableEditor({ ...props }: FullPageEditorPropsNext): React.JSX.Element {
	const fullPagePreset = useFullPageEditorPreset({
		appearance: props.appearance,
		featureFlags: props.featureFlags,
		onSave: props.onSave,
		smartCardContext: isSSRStreaming() ? props.smartCardContext : undefined,
		linking: props.linking,
		contextIdentifierProvider: props.contextIdentifierProvider,
		media: props.media,
		placeholder: props.placeholder,
		collabEdit: props.collabEdit,
		primaryToolbarComponents: props.primaryToolbarComponents,
		waitForMediaUpload: props.waitForMediaUpload,
		mention: props.mention,
		allowFindReplace: props.allowFindReplace,
		allowExtension: props.allowExtension,
		extensionHandlers: props.extensionHandlers,
		referentialityContext: props.referentialityContext,
		emojiProvider: props.emojiProvider,
		mentionProvider: props.mentionProvider,
		annotationProviders: props.annotationProviders,
		autoformattingProvider: props.autoformattingProvider,
		taskDecisionProvider: props.taskDecisionProvider,
		editorPluginAIOptions: props.editorPluginAIOptions,
		aiGlobalOptIn: props.aiGlobalOptIn,
		editorPluginAIFeedbackHandlers: props.editorPluginAIFeedbackHandlers,
		elementBrowser: props.elementBrowser,
		setEditorAPI: props.setEditorAPI,
		viewMode: props.viewMode,
		hasEditPermission: props.hasEditPermission,
		requestToEditContent: props.requestToEditContent,
		hasRequestedEditPermission: props.hasRequestedEditPermission,
		getHeadingLinkPath: props.getHeadingLinkPath,
		shouldHideHeadingAnchorLinks: props.shouldHideHeadingAnchorLinks,
		__rendererExtensionOptions: props.__rendererExtensionOptions,
		__isRemixHidden: props.__isRemixHidden,
		__isRovoLLMEnabled: props.__isRovoLLMEnabled,
		__remixDisabledReason: props.__remixDisabledReason,
		__livePage: props.__livePage,
		flags: props.flags,
		quickInsert: props.quickInsert,
		contextPanelOptions: props.contextPanelOptions,
		spaceId: props.spaceId,
		spaceKey: props.spaceKey,
		contentId: props.contentId,
		isAIEnabledForContent: props.isAIEnabledForContent,
		getExtensionHeight: props.getExtensionHeight,
		collabEditProvider: props.collabEditProvider,
		disabled: props.disabled,
		contextualFormattingEnabled:
			!!props.showEmbeddedModernHeader || !!props.contextualFormattingMode,
		contextualFormattingMode: props.contextualFormattingMode,
		enableAIPlannerRemoteTextHighlight: props.enableAIPlannerRemoteTextHighlight,
		isCompanyHub: props.isCompanyHub,
		syncedBlockProvider: props.syncedBlockProvider,
		contentMode: props.contentMode,
		snippetExtensionList: props.snippetExtensionList,
		syncedBlockEventHandlers: props.syncedBlockEventHandlers,
		isEmbeddedConfluence: props.isEmbeddedConfluence,
		contentType: props.contentType,
		onShowSuggestionsSidebar: props.onShowSuggestionsSidebar,
	});

	const editorProps: PatchedFullPageEditorProps = {
		preset: fullPagePreset,
		defaultValue: props.defaultValue,
		disabled: props.disabled,
		// Providers
		mentionProvider: props.mentionProvider,
		contextIdentifierProvider: props.contextIdentifierProvider,
		searchProvider: props.searchProvider,
		activityProvider: props.activityProvider,
		annotationProviders: props.annotationProviders,
		collabEditProvider: props.collabEditProvider,
		collabEdit: props.collabEdit,
		extensionProviders: props.extensionProviders,
		// Other props
		shouldFocus: props.shouldFocus,
		quickInsert: props.quickInsert,
		secondaryToolbarComponents: props.secondaryToolbarComponents,
		featureFlags: props.featureFlags,
		primaryToolbarComponents: props.primaryToolbarComponents,
		contextPanel: props.contextPanel,
		contentComponents: props.contentComponents,
		primaryToolbarIconBefore: props.primaryToolbarIconBefore,
		sanitizePrivateContent: props.sanitizePrivateContent,
		__livePage: props.__livePage,
		// Callbacks
		// @ts-expect-error - We don't allow `EditorView` to be passed in the new API
		onChange: props.onChange,
		onEditorReady: props.onEditorReady,
		onSSRMeasure: props.onSSRMeasure,
		appearance: props.appearance,
		contentMode: props.contentMode,
		getExtensionHeight: props.getExtensionHeight,
	};

	return <FullPageEditor {...editorProps} />;
}
